import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import LiveScore from "./tabs/LiveScore";
import Scorecard from "./tabs/Scorecard";
import MatchSummary from "./tabs/MatchSummary";
import TeamSheet from "./tabs/TeamSheet";
import Commentary from "./tabs/Commentary";
import HomePage from "./pages/HomePage";
import MyMatches from "./pages/MyMatches";
import Leagues from "./pages/Leagues";
import Settings from "./pages/Settings";
import Profile from "./pages/Profile";
import About from "./pages/About";
import Rules from "./pages/Rules";
import TermsConditions from "./pages/TermsConditions";
import UserAgreement from "./pages/UserAgreement";
import Support from "./pages/Support";
import CricketSquad from "./pages/CricketSquad";
import MyStats from "./pages/MyStats";
import Teams from "./pages/Teams";
import DebugSupabase from "./pages/DebugSupabase";
import AuthCallback from "./pages/AuthCallback";
import PublicHomePage from "./pages/PublicHomePage";
import PublicMatchPage from "./pages/PublicMatchPage";
import LiveMatchesPage from "./pages/LiveMatchesPage";
import FixturesPage from "./pages/FixturesPage";
import ResultsPage from "./pages/ResultsPage";
import LeaguesPublicPage from "./pages/LeaguesPublicPage";
import LeagueDetailPage from "./pages/LeagueDetailPage";
import AuthDebug from "./pages/AuthDebug";
import watchwicketLogo from "./assets/file_00000000711072438e9d72dabae9eda2.png";
import { useAuth } from "./contexts/AuthContext";
import AuthModal from "./components/AuthModal";
import Watermark from "./components/Watermark";
import TeamLogo from "./components/broadcast/TeamLogo";
import MatchSharingModal from "./components/MatchSharingModal";
import StartScheduleModal from "./components/StartScheduleModal";
import BottomNav, { NavScreen } from "./components/BottomNav";
import AppShell from "./components/layout/AppShell";
import ErrorBoundary from "./components/ErrorBoundary";
import MatchFormatSelector from "./components/MatchFormatSelector";
import TeamFormatSelector from "./components/TeamFormatSelector";
import HamburgerMenu from "./components/HamburgerMenu";
import MoreMenu from "./components/MoreMenu";
import AccountMenu from "./components/AccountMenu";

import { MatchState, MatchFormat } from "./matchTypes";
import { createNewMatch, applyFormat, getRequiredRunRate } from "./engine/MatchEngine";
import {
  loadMatchFromDb,
  saveMatchToDb,
  deleteMatchFromDb,
  createNewMatchInDb,
  listMatchesFromDb,
} from "./store/supabaseMatches";
import { clampOversToFormat } from "./utils/oversConstraints";
import {
  saveMatchLocally,
  markMatchAsSynced,
  markMatchAsUnsynced,
  removeMatchLocally,
  getAllUnsyncedMatches,
} from "./store/matchPersistence";
import { LeagueFixture, League } from "./store/leagues";
import { matchFormatToSquadFormat } from "./store/teams";

type TabKey = "live" | "scorecard" | "summary" | "teams" | "commentary";

export default function App() {
  const currentPath = window.location.pathname;
  const isAuthCallback = currentPath === '/auth/callback';
  const isAuthDebug = currentPath === '/auth-debug';

  if (isAuthDebug) {
    return <AuthDebug />;
  }

  if (isAuthCallback) {
    return <AuthCallback />;
  }

  const matchIdMatch = currentPath.match(/^\/match\/(.+)$/);
  const isPublicMatch = matchIdMatch !== null;
  const isPublicHome = currentPath === '/' || currentPath === '';
  const isScoreBox = currentPath.startsWith('/scorebox');

  if (isPublicMatch && matchIdMatch) {
    const matchId = matchIdMatch[1];
    return <PublicMatchPage matchId={matchId} />;
  }

  if (currentPath === '/live') return <LiveMatchesPage />;
  if (currentPath === '/fixtures') return <FixturesPage />;
  if (currentPath === '/results') return <ResultsPage />;
  if (currentPath === '/leagues') return <LeaguesPublicPage />;

  const leagueIdMatch = currentPath.match(/^\/leagues\/(.+)$/);
  if (leagueIdMatch) {
    return <LeagueDetailPage leagueId={leagueIdMatch[1]} />;
  }

  const { user, profile, isPro, loading, signOut } = useAuth();

  if (!user && isPublicHome) {
    return <PublicHomePage />;
  }
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [showStartScheduleModal, setShowStartScheduleModal] = useState(false);
  const [showTeamFormatPicker, setShowTeamFormatPicker] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<MatchFormat | null>(null);
  const [showMatchSharing, setShowMatchSharing] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showUserAgreement, setShowUserAgreement] = useState(false);
  const [showCricketSquad, setShowCricketSquad] = useState(false);
  const [showMyStats, setShowMyStats] = useState(false);
  const [showDebugSupabase, setShowDebugSupabase] = useState(false);

  const [navScreen, setNavScreen] = useState<NavScreen>("home");
  const [myMatchesInitialCategory, setMyMatchesInitialCategory] = useState<"ongoing" | "upcoming" | "completed" | null>(null);
  const [currentMatchId, setCurrentMatchId] = useState<string | null>(null);
  const resumeInProgressRef = useRef(false);
  const leaveNavInProgressRef = useRef(false);
  const [state, setState] = useState<MatchState>(() => createNewMatch());
  const [tab, setTab] = useState<TabKey>("live");

  // --- V216: Browser history integration for Android Back ---
  // A lightweight stack of navigation snapshots stored in browser history.
  // Only presentational state (which screen/tab/modal is visible) is tracked.
  // Match/scoring state is NEVER directly mutated by popstate — the existing
  // safe leave-match handler is reused to save and clear the match UI context.
  interface NavSnapshot {
    navScreen: NavScreen;
    tab: TabKey;
    hasMatch: boolean;
    // Only persistent modals are tracked. Transient workflow modals
    // (modeSelector, startSchedule, teamFormatPicker) are excluded so popstate
    // can never resurrect them after the workflow has completed.
    modal: 'none' | 'menu' | 'more' | 'account' | 'settings' | 'profile'
      | 'about' | 'rules' | 'terms' | 'userAgreement' | 'cricketSquad' | 'myStats' | 'matchSharing';
  }

  const navStateRef = useRef<NavSnapshot | null>(null);
  const isRestoringRef = useRef(false);
  const leaveMatchRef = useRef<(() => void) | null>(null);

  const currentModal = useCallback((): NavSnapshot['modal'] => {
    if (showMenu) return 'menu';
    if (showMore) return 'more';
    if (showAccount) return 'account';
    if (showSettings) return 'settings';
    if (showProfile) return 'profile';
    if (showAbout) return 'about';
    if (showRules) return 'rules';
    if (showTerms) return 'terms';
    if (showUserAgreement) return 'userAgreement';
    if (showCricketSquad) return 'cricketSquad';
    if (showMyStats) return 'myStats';
    if (showMatchSharing) return 'matchSharing';
    return 'none';
  }, [showMenu, showMore, showAccount, showSettings, showProfile, showAbout, showRules, showTerms, showUserAgreement, showCricketSquad, showMyStats, showMatchSharing]);

  const snapshotNavState = useCallback((): NavSnapshot => ({
    navScreen,
    tab,
    hasMatch: !!currentMatchId,
    modal: currentModal(),
  }), [navScreen, tab, currentMatchId, currentModal]);

  const closeAllModals = useCallback(() => {
    setShowMenu(false);
    setShowMore(false);
    setShowAccount(false);
    setShowSettings(false);
    setShowProfile(false);
    setShowModeSelector(false);
    setShowStartScheduleModal(false);
    setShowTeamFormatPicker(false);
    setShowAbout(false);
    setShowRules(false);
    setShowTerms(false);
    setShowUserAgreement(false);
    setShowCricketSquad(false);
    setShowMyStats(false);
    setShowMatchSharing(false);
  }, []);

  // Push a new history entry whenever presentational nav state changes.
  // The isRestoringRef flag is reset here (not via setTimeout) so rapid
  // Back presses cannot race the reset and re-push an entry.
  useEffect(() => {
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      return;
    }
    const snap = snapshotNavState();
    const prev = navStateRef.current;
    if (prev && JSON.stringify(prev) === JSON.stringify(snap)) return;
    navStateRef.current = snap;
    const path = window.location.pathname;
    if (path !== '/' && path !== '/scorebox') return;
    window.history.pushState(snap, '');
  }, [snapshotNavState]);

  // Popstate handler: restore the previous presentational state from history.
  // For match tab changes (hasMatch=true), restores the tab.
  // For leaving a match (hasMatch=false while currentMatchId is set), reuses
  // the existing safe leave-match handler to save the match to the database,
  // clear the match UI context, and navigate to My Matches. The match remains
  // fully resumable with identical scoring state.
  useEffect(() => {
    const handlePopState = () => {
      const snap = window.history.state as NavSnapshot | null;
      if (!snap || typeof snap.navScreen !== 'string') return;

      isRestoringRef.current = true;
      navStateRef.current = snap;

      closeAllModals();

      if (snap.modal !== 'none') {
        switch (snap.modal) {
          case 'menu': setShowMenu(true); break;
          case 'more': setShowMore(true); break;
          case 'account': setShowAccount(true); break;
          case 'settings': setShowSettings(true); break;
          case 'profile': setShowProfile(true); break;
          case 'about': setShowAbout(true); break;
          case 'rules': setShowRules(true); break;
          case 'terms': setShowTerms(true); break;
          case 'userAgreement': setShowUserAgreement(true); break;
          case 'cricketSquad': setShowCricketSquad(true); break;
          case 'myStats': setShowMyStats(true); break;
          case 'matchSharing': setShowMatchSharing(true); break;
        }
      }

      setTab(snap.tab);
      setNavScreen(snap.navScreen);

      // Leaving match: snapshot has no match but currentMatchId is set.
      // Reuse the existing safe leave-match handler to save the match,
      // clear the match UI context, and go to My Matches.
      if (!snap.hasMatch && currentMatchId) {
        if (leaveMatchRef.current) {
          leaveMatchRef.current();
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [closeAllModals, currentMatchId]);
  const [currentLeagueId, setCurrentLeagueId] = useState<string | null>(null);
  const [currentFixtureId, setCurrentFixtureId] = useState<string | null>(null);
  const [currentFixtureHomeTeamId, setCurrentFixtureHomeTeamId] = useState<string | null>(null);
  const [currentFixtureAwayTeamId, setCurrentFixtureAwayTeamId] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Refs for autosave logic
  const isInitialLoadRef = useRef(true); // Prevent autosave during initial load
  const lastSavedKeyRef = useRef<string | null>(null); // Track last saved state to prevent duplicate saves

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const normalizeMatchState = useCallback((matchState: MatchState): { state: MatchState; wasAdjusted: boolean } => {
    let wasAdjusted = false;

    if (matchState.format === "INDOOR") {
      matchState.teamAPlayers.forEach(p => {
        if (p.outsCount === undefined) {
          p.outsCount = 0;
        }
      });
      matchState.teamBPlayers.forEach(p => {
        if (p.outsCount === undefined) {
          p.outsCount = 0;
        }
      });
    }

    const clampedOvers = clampOversToFormat(matchState.oversLimit, matchState.format);
    if (clampedOvers !== matchState.oversLimit) {
      matchState.oversLimit = clampedOvers;
      if (matchState.innings.length > 0) {
        matchState.innings[matchState.currentInnings].oversLimit = clampedOvers;
      }
      wasAdjusted = true;
    }

    return { state: matchState, wasAdjusted };
  }, []);

  useEffect(() => {
    async function loadMatches() {
      if (!user) return;

      const RESUME_PROMPT_SESSION_KEY = 'ww_resume_prompt_shown';
      if (sessionStorage.getItem(RESUME_PROMPT_SESSION_KEY) === '1') {
        console.log('[Resume] Prompt already handled this session, skipping.');
        return;
      }

      const unsyncedMatches = getAllUnsyncedMatches();
      const unsyncedForThisUser = unsyncedMatches.filter(m => m.userId === user.id);
      console.log('[Resume] Local unsynced matches for user:', unsyncedForThisUser.length);
      if (unsyncedForThisUser.length === 0) return;

      // Reconcile with server: any local draft whose matchId exists on Supabase
      // has already been synced in a previous session — mark it synced and skip prompting.
      let serverIds = new Set<string>();
      try {
        const serverMatches = await listMatchesFromDb();
        serverIds = new Set(serverMatches.map(m => m.id));
        console.log('[Resume] Server match count:', serverIds.size);
      } catch (e) {
        console.warn('[Resume] Could not fetch server matches for reconciliation:', e);
      }

      const trulyLocalOnly = unsyncedForThisUser.filter(m => {
        if (serverIds.has(m.matchId)) {
          console.log('[Resume] Reconciling local draft with server copy:', m.matchId);
          markMatchAsSynced(m.matchId);
          return false;
        }
        return true;
      });

      if (trulyLocalOnly.length === 0) {
        console.log('[Resume] No genuinely local-only drafts after reconciliation.');
        return;
      }

      const latestUnsynced = trulyLocalOnly[0];
      const matchState = latestUnsynced.state;

      const legalBalls = matchState.innings.reduce((total, inning) => {
        return total + inning.deliveries.filter(d => d.isLegal).length;
      }, 0);

      const isResumable =
        matchState.status === 'live' ||
        (matchState.status === 'draft' && matchState.hasActivity === true && legalBalls > 0);

      const shouldNotResume =
        matchState.status === 'abandoned' ||
        matchState.status === 'deleted' ||
        matchState.status === 'completed' ||
        matchState.status === 'Completed' ||
        matchState.deletedAt;

      console.log('[Resume] Candidate draft:', {
        matchId: latestUnsynced.matchId,
        status: matchState.status,
        legalBalls,
        hasActivity: matchState.hasActivity,
        isResumable,
        shouldNotResume,
      });

      if (!isResumable || shouldNotResume) {
        console.log('[Resume] Draft not resumable, removing local copy.');
        removeMatchLocally(latestUnsynced.matchId);
        return;
      }

      sessionStorage.setItem(RESUME_PROMPT_SESSION_KEY, '1');

      const shouldRestore = window.confirm(
        `You have an unsaved match: ${matchState.teamAName} vs ${matchState.teamBName}. Would you like to resume it?`
      );

      if (!shouldRestore) {
        console.log('[Resume] User declined. Removing local draft:', latestUnsynced.matchId);
        removeMatchLocally(latestUnsynced.matchId);
        return;
      }

      const { state: normalizedState, wasAdjusted } = normalizeMatchState(matchState);
      setCurrentMatchId(latestUnsynced.matchId);
      setState(normalizedState);
      if (wasAdjusted) {
        setSyncError("Overs were adjusted to match mode limits.");
      }

      try {
        const syncResult = await saveMatchToDb(latestUnsynced.matchId, normalizedState);
        if (syncResult.success) {
          markMatchAsSynced(latestUnsynced.matchId);
          setSyncError(null);
          console.log('[Resume] Resumed and synced draft:', latestUnsynced.matchId);
        } else {
          console.warn('[Resume] Resume sync failed; keeping local copy:', syncResult.error);
        }
      } catch (e) {
        console.warn('[Resume] Resume sync threw:', e);
      }
    }

    loadMatches();
  }, [user, normalizeMatchState]);

  useEffect(() => {
    if (showMenu || showMore || showAccount) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showMenu, showMore, showAccount]);

  // Create a stable "save key" that only changes when meaningful data changes
  // This prevents infinite loops by not depending on the entire state object
  const saveKey = useMemo(() => {
    if (!currentMatchId) return null;

    // Summarize each innings: balls, runs, wickets, deliveries length, completed
    // This catches every scoring event, wicket, innings-end, and 2nd-innings start
    const inningsHash = state.innings
      .map(
        (i) =>
          `${i.battingTeam}:${i.totalBalls}/${i.totalRuns}/${i.wickets}/${i.deliveries.length}/${i.completed ? 1 : 0}`
      )
      .join('|');
    const playersHash = `${state.teamAPlayers.length}-${state.teamBPlayers.length}`;

    return JSON.stringify({
      matchId: currentMatchId,
      status: state.status,
      format: state.format,
      teamAName: state.teamAName,
      teamBName: state.teamBName,
      oversLimit: state.oversLimit,
      currentInnings: state.currentInnings,
      strikerId: state.strikerId,
      nonStrikerId: state.nonStrikerId,
      bowlerId: state.bowlerId,
      inningsHash,
      playersHash,
      tossWinner: state.toss?.winnerTeam || null,
      tossDecision: state.toss?.decision || null,
    });
  }, [
    currentMatchId,
    state.status,
    state.format,
    state.teamAName,
    state.teamBName,
    state.oversLimit,
    state.currentInnings,
    state.strikerId,
    state.nonStrikerId,
    state.bowlerId,
    state.innings,
    state.teamAPlayers.length,
    state.teamBPlayers.length,
    state.toss?.winnerTeam,
    state.toss?.decision,
  ]);

  // Autosave with debouncing and change detection to prevent infinite loops
  useEffect(() => {
    if (!currentMatchId || !user) return;

    const isCompleted = state.status === 'completed' || state.status === 'Completed';
    const legalBallsCount = state.innings.reduce((t, inn) => t + inn.deliveries.filter(d => d.isLegal).length, 0);
    // A draft match with a toss set or balls bowled is effectively active and should sync
    const isDraftWithNoActivity = (state.status === 'draft' || !state.status) && !state.toss?.winnerTeam && legalBallsCount === 0;

    // Always save locally immediately (no debounce for local storage)
    saveMatchLocally(currentMatchId, state, user.id, false);

    // Don't auto-sync completed matches or truly empty drafts (no toss, no balls)
    if (isCompleted || isDraftWithNoActivity) {
      if (isCompleted) {
        // completed matches: still mark initial load as done
      }
      isInitialLoadRef.current = false;
      return;
    }

    // Skip autosave during initial load (prevents overwriting DB with default values)
    if (isInitialLoadRef.current) {
      console.log('[App] Skipping autosave during initial load');
      isInitialLoadRef.current = false;
      lastSavedKeyRef.current = saveKey;
      return;
    }

    // Skip if nothing changed (same saveKey as last save)
    if (saveKey === lastSavedKeyRef.current) {
      return;
    }

    // Only auto-sync 'live' matches with debouncing
    const saveTimeout = setTimeout(async () => {
      // Double-check the key hasn't changed during debounce
      if (saveKey === lastSavedKeyRef.current) {
        return;
      }

      const trySave = async () => saveMatchToDb(currentMatchId, state);

      try {
        console.log('[App] Autosave start', { matchId: currentMatchId, status: state.status, currentInnings: state.currentInnings });
        let result = await trySave();
        if (!result.success) {
          // One quick retry for transient network / RLS refresh issues
          await new Promise((r) => setTimeout(r, 500));
          console.warn('[App] Autosave retrying after failure:', result.error);
          result = await trySave();
        }
        if (result.success) {
          lastSavedKeyRef.current = saveKey;
          markMatchAsSynced(currentMatchId);
          setSyncError(null);
          console.log('[App] Autosave OK', { matchId: currentMatchId });
        } else {
          markMatchAsUnsynced(currentMatchId);
          setSyncError("Unable to sync match to server. Your data is saved locally.");
          console.error('[App] Autosave FAILED after retry:', result.error);
        }
      } catch (err) {
        console.error('[App] Auto-save error:', err);
        markMatchAsUnsynced(currentMatchId);
        setSyncError("Unable to sync match to server. Your data is saved locally.");
      }
    }, 1000); // Debounce: wait 1 second after last change

    // Cleanup: cancel pending save if component unmounts or deps change
    return () => clearTimeout(saveTimeout);
  }, [saveKey, currentMatchId, user, state]);

  function update<K extends keyof MatchState>(key: K, val: MatchState[K]) {
    setState((s) => ({ ...s, [key]: val, updatedAt: new Date().toISOString() }));
  }

  function onChangeFormat(f: MatchFormat) {
    const hasStarted = state.innings.some(inning => inning.deliveries.length > 0);

    if (hasStarted) {
      alert("Cannot change match format once the match has started. Please start a new match to use a different format.");
      return;
    }

    setState((s) => {
      const copy = { ...s, format: f };
      applyFormat(copy, f);
      return { ...copy };
    });
  }

  function handleNavigate(screen: NavScreen) {
    if (screen === "newMatch") {
      handleNewMatch();
    } else if (screen === "newTeam") {
      setShowTeamFormatPicker(true);
    } else if (screen === "newLeague") {
      setNavScreen("leagues");
    } else if (screen === "more") {
      setShowMore(true);
    } else {
      setNavScreen(screen);
    }
  }

  // V216: Push initial history entry for the authenticated ScoreBox home.
  // This ensures there's a baseline entry that Android Back can return to
  // before exiting the app.
  useEffect(() => {
    if (!user) return;
    const path = window.location.pathname;
    if (path !== '/' && path !== '/scorebox') return;
    // Replace the initial entry with a proper snapshot so popstate has data
    if (!navStateRef.current) {
      const snap = snapshotNavState();
      navStateRef.current = snap;
      window.history.replaceState(snap, '');
    }
  }, [user, snapshotNavState]);

  async function handleNewMatch() {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setShowModeSelector(true);
  }

  async function handleCreateMatchWithMode(format: MatchFormat) {
    setShowModeSelector(false);
    setSelectedFormat(format);
    setShowStartScheduleModal(true);
  }

  async function handleStartNow() {
    if (!selectedFormat) return;

    setShowStartScheduleModal(false);

    const newState = createNewMatch();
    newState.format = selectedFormat;
    newState.status = "draft";
    newState.isScheduled = false;
    applyFormat(newState, newState.format);

    const result = await createNewMatchInDb(newState);
    if (result.success && result.matchId) {
      // Reset autosave guard when creating new match
      isInitialLoadRef.current = true;
      lastSavedKeyRef.current = null;
      setCurrentMatchId(result.matchId);
      setState(newState);
      setTab("teams");
    } else {
      alert('Failed to create match: ' + (result.error || 'Unknown error'));
    }
    setSelectedFormat(null);
  }

  async function handleSchedule(location: string, date: string, time: string) {
    if (!selectedFormat) return;

    setShowStartScheduleModal(false);

    const newState = createNewMatch();
    newState.format = selectedFormat;
    newState.status = "scheduled";
    newState.isScheduled = true;
    newState.matchLocation = location;
    newState.matchDate = date;
    newState.matchTime = time;
    applyFormat(newState, newState.format);

    const result = await createNewMatchInDb(newState);
    if (result.success && result.matchId) {
      // Reset autosave guard when creating new match
      isInitialLoadRef.current = true;
      lastSavedKeyRef.current = null;
      setCurrentMatchId(result.matchId);
      setState(newState);
      setTab("teams");
    } else {
      alert('Failed to create match: ' + (result.error || 'Unknown error'));
    }
    setSelectedFormat(null);
  }

  async function handleResumeMatch(id: string) {
    if (resumeInProgressRef.current) {
      console.log('[handleResumeMatch] Already resuming, ignoring duplicate call for:', id);
      return;
    }

    console.log('[handleResumeMatch] Resume button clicked, matchId:', id);
    resumeInProgressRef.current = true;

    try {
      console.log('[handleResumeMatch] Loading from Supabase, matchId:', id);
      const matchState = await loadMatchFromDb(id);
      console.log('[handleResumeMatch] Loaded match state:', matchState ? `status=${matchState.status}` : 'null (not found)');

      if (!matchState) {
        console.error('[handleResumeMatch] Match not found in Supabase for id:', id);
        alert('Could not load match. Please try again.');
        return;
      }

      if (!Array.isArray(matchState.innings)) {
        console.error('[handleResumeMatch] Invalid innings structure, cannot resume:', id);
        alert('Match data is corrupted. Unable to resume.');
        return;
      }

      if (matchState.format === "INDOOR") {
        matchState.teamAPlayers.forEach(p => {
          if (p.outsCount === undefined) p.outsCount = 0;
        });
        matchState.teamBPlayers.forEach(p => {
          if (p.outsCount === undefined) p.outsCount = 0;
        });
      }

      isInitialLoadRef.current = true;
      lastSavedKeyRef.current = null;

      const targetTab = (matchState.status === 'Completed' || matchState.status === 'completed') ? 'summary' : 'live';
      console.log('[handleResumeMatch] Navigation target tab:', targetTab, '— setting match state now');

      setNavScreen("home");
      setCurrentMatchId(id);
      setState(matchState);
      setTab(targetTab);

      console.log('[handleResumeMatch] Done — matchId set, navScreen=home, tab=' + targetTab);
    } catch (err) {
      console.error('[handleResumeMatch] Error loading match from DB:', err);
      alert('Match data corrupted. Unable to resume.');
    } finally {
      resumeInProgressRef.current = false;
    }
  }

  async function handleDeleteMatch(id: string) {
    const result = await deleteMatchFromDb(id);
    if (result.success) {
      if (currentMatchId === id) {
        setCurrentMatchId(null);
        setState(createNewMatch());
      }
    } else {
      alert('Failed to delete match: ' + (result.error || 'Unknown error'));
    }
  }

  // V217: leaveMatchRef gives the popstate handler access to this safe
  // leave-match function without it being a dependency of the effect.
  // On save failure, the match stays loaded and the user remains on the
  // current match — currentMatchId and state are NOT cleared.
  leaveMatchRef.current = async () => {
    if (currentMatchId && user) {
      const isCompleted = state.status === 'completed' || state.status === 'Completed';
      if (!isCompleted) {
        let saveResult: { success: boolean; error?: string } | null = null;
        try {
          saveResult = await saveMatchToDb(currentMatchId, state);
        } catch (err) {
          console.error('[V217] Error saving match on back:', err);
        }
        if (!saveResult || !saveResult.success) {
          markMatchAsUnsynced(currentMatchId);
          setSyncError(
            "Couldn't save the match before leaving. Your scoring is preserved. Check your connection and try again."
          );
          setTab(tab);
          const matchSnap: NavSnapshot = {
            navScreen: "home",
            tab,
            hasMatch: true,
            modal: "none",
          };
          navStateRef.current = matchSnap;
          isRestoringRef.current = false;
          window.history.pushState(matchSnap, '');
          return;
        }
        markMatchAsSynced(currentMatchId);
      }
    }
    setCurrentMatchId(null);
    setState(createNewMatch());
    setNavScreen("myMatches");
    setSyncError(null);
  };

  // Save the active match (if any) and return to the non-match AppShell view.
  // This is the single safe path from the scoring/match-setup screen back to
  // any nav destination. The match stays Live/Completed in the database and is
  // fully resumable via My Matches — no scoring state is lost.
  //
  // On save failure the user stays on the scoring screen with an error message
  // so they can retry — currentMatchId and state are NOT cleared. This mirrors
  // the proven leaveMatchRef (popstate) handler safety pattern.
  // A re-entrancy guard prevents overlapping saves from repeated menu taps.
  async function handleLeaveMatchAndNavigate(screen: NavScreen) {
    if (leaveNavInProgressRef.current) {
      console.log('[handleLeaveMatchAndNavigate] Already in progress, ignoring duplicate call');
      return;
    }

    if (currentMatchId && user) {
      const isCompleted = state.status === 'completed' || state.status === 'Completed';

      if (!isCompleted) {
        leaveNavInProgressRef.current = true;
        let saveResult: { success: boolean; error?: string } | null = null;
        try {
          saveResult = await saveMatchToDb(currentMatchId, state);
        } catch (err) {
          console.error('[handleLeaveMatchAndNavigate] Error saving match:', err);
        } finally {
          leaveNavInProgressRef.current = false;
        }

        if (!saveResult || !saveResult.success) {
          markMatchAsUnsynced(currentMatchId);
          setSyncError(
            "Couldn't save the match before leaving. Your scoring is preserved. Check your connection and try again."
          );
          return;
        }
        markMatchAsSynced(currentMatchId);
      }
    }
    setCurrentMatchId(null);
    setState(createNewMatch());
    setNavScreen(screen);
    setSyncError(null);
  }

  async function handleBackToMatches() {
    await handleLeaveMatchAndNavigate("home");
  }

  async function handleDiscardMatch() {
    if (!currentMatchId || !user) return;

    const isCompleted = state.status === 'completed' || state.status === 'Completed';

    if (isCompleted) {
      alert("Cannot discard a completed match. Completed matches are immutable.");
      return;
    }

    const shouldDiscard = window.confirm(
      "Are you sure you want to discard this match? This cannot be undone."
    );

    if (!shouldDiscard) {
      return;
    }

    // Mark match as abandoned
    const abandonedState = { ...state, status: 'abandoned' as const };

    try {
      await saveMatchToDb(currentMatchId, abandonedState);
      removeMatchLocally(currentMatchId);
    } catch (err) {
      console.error('Error abandoning match:', err);
    }

    // Reset to home
    setCurrentMatchId(null);
    setState(createNewMatch());
    setNavScreen("home");
    setSyncError(null);
  }

  async function handleSaveSetup() {
    if (!currentMatchId || !user) return;

    const draftState = { ...state, status: 'draft' as const };

    saveMatchLocally(currentMatchId, draftState, user.id, false);

    try {
      const result = await saveMatchToDb(currentMatchId, draftState);

      if (result.success) {
        markMatchAsSynced(currentMatchId);
        setSyncError(null);
      } else {
        throw new Error(result.error || 'Failed to save setup');
      }
    } catch (err) {
      console.error('Error saving setup:', err);
      markMatchAsUnsynced(currentMatchId);
      throw err;
    }
  }

  async function handleStartMatch() {
    if (!currentMatchId || !user) return;

    const scoringState = { ...state };

    saveMatchLocally(currentMatchId, scoringState, user.id, false);

    setState(scoringState);
    setTab("live");

    const liveSnap: NavSnapshot = {
      navScreen: "home",
      tab: "live",
      hasMatch: true,
      modal: "none",
    };
    navStateRef.current = liveSnap;
    window.history.replaceState(liveSnap, '');

    try {
      const result = await saveMatchToDb(currentMatchId, scoringState);

      if (result.success) {
        markMatchAsSynced(currentMatchId);
        setSyncError(null);
      } else {
        console.error('[handleStartMatch] DB save failed:', result.error);
        markMatchAsUnsynced(currentMatchId);
        setSyncError("Match started but couldn't sync to server yet. Will retry automatically.");
      }
    } catch (err) {
      console.error('[handleStartMatch] Exception during save:', err);
      markMatchAsUnsynced(currentMatchId);
      setSyncError("Match started but couldn't sync to server yet. Will retry automatically.");
    }
  }

  async function handleMatchComplete() {
    if (!currentMatchId || !user) return;

    const completedState = { ...state, status: 'completed' as const };

    saveMatchLocally(currentMatchId, completedState, user.id, false);

    try {
      const result = await saveMatchToDb(currentMatchId, completedState);

      if (!result.success) {
        const isNetworkError = !navigator.onLine || result.error?.includes('fetch');
        const isAuthError = result.error?.toLowerCase().includes('sign in') ||
                           result.error?.toLowerCase().includes('permission');

        let errorMessage: string;
        if (isAuthError) {
          errorMessage = "Save blocked. Please sign in again to save this completed match. Your data is stored locally.";
        } else if (isNetworkError) {
          errorMessage = "We couldn't save this match due to a network issue, but the full match data is stored on this device. Please try saving again when you have a better connection.";
        } else {
          errorMessage = `Save failed: ${result.error || 'Unknown error'}`;
        }

        console.error('[handleMatchComplete] Supabase error:', result.error);
        setSyncError(errorMessage);
        return;
      }

      removeMatchLocally(currentMatchId);
      setSyncError(null);
    } catch (err) {
      console.error('[handleMatchComplete] Exception:', err);
      const errorMsg = err instanceof Error ? err.message : String(err);
      const isNetworkError = !navigator.onLine || errorMsg.includes('fetch') || errorMsg.includes('network');
      const displayMessage = isNetworkError
        ? "We couldn't save this match due to a network issue, but the full match data is stored on this device. Please try saving again when you have a better connection."
        : `Save failed: ${errorMsg}`;
      setSyncError(displayMessage);
    }
  }

  async function handleStartMatchFromFixture(fixture: LeagueFixture, league: League) {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    const newState = createNewMatch();
    newState.format = league.format as MatchFormat;
    newState.teamAName = fixture.team_a_name || fixture.home_team_name || "Home Team";
    newState.teamBName = fixture.team_b_name || fixture.away_team_name || "Away Team";
    applyFormat(newState, newState.format);

    const result = await createNewMatchInDb(newState, league.id, fixture.id, fixture.season_id || undefined);
    if (result.success && result.matchId) {
      setCurrentMatchId(result.matchId);
      setCurrentLeagueId(league.id);
      setCurrentFixtureId(fixture.id);
      setCurrentFixtureHomeTeamId(fixture.home_team_id);
      setCurrentFixtureAwayTeamId(fixture.away_team_id);
      setState(newState);
      setTab("teams");
    } else {
      alert('Failed to create match: ' + (result.error || 'Unknown error'));
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading WatchWicket ScoreBox...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="mb-6">
            <img
              src={watchwicketLogo}
              alt="WatchWicket Logo"
              className="w-48 h-48 object-contain mx-auto mb-6 drop-shadow-2xl filter brightness-110"
            />
            <h1 className="text-3xl font-bold mb-2">WatchWicket ScoreBox</h1>
            <p className="text-slate-400 mb-6">Professional Cricket Scoring App</p>
          </div>
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
            <h2 className="text-xl font-semibold mb-4">Sign in to continue</h2>
            <p className="text-slate-300 text-sm mb-6">
              Create an account or sign in to start scoring cricket matches
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Sign In / Sign Up
            </button>
          </div>
        </div>
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </div>
    );
  }

  if (!currentMatchId) {
    return (
      <>
        <AppShell
          navScreen={navScreen}
          onNavigate={handleNavigate}
          onOpenMenu={() => setShowMenu(true)}
          onOpenProfile={() => setShowProfile(true)}
          onOpenSettings={() => setShowSettings(true)}
          onOpenAccount={() => setShowAccount(true)}
        >
          {navScreen === "home" && (
            <HomePage
              onOpenMenu={() => setShowMenu(true)}
              onOpenAccount={() => setShowAccount(true)}
              onNavigateToStats={() => setShowMyStats(true)}
              onStartMatch={handleNewMatch}
              onNavigate={(s) => handleNavigate(s as NavScreen)}
              onResumeMatch={handleResumeMatch}
              onViewAllMatches={(tab) => {
                setMyMatchesInitialCategory(tab);
                setNavScreen("myMatches");
              }}
            />
          )}
          {navScreen === "myMatches" && (
            <ErrorBoundary>
              <MyMatches
                onClose={() => { setNavScreen("home"); setMyMatchesInitialCategory(null); }}
                onViewMatch={handleResumeMatch}
                initialCategory={myMatchesInitialCategory}
              />
            </ErrorBoundary>
          )}
          {navScreen === "players" && <CricketSquad onBack={() => setNavScreen("home")} />}
          {navScreen === "teams" && <Teams format="indoor" onClose={() => setNavScreen("home")} autoCreate={false} />}
          {navScreen === "teamsIndoor" && <Teams format="indoor" onClose={() => setNavScreen("home")} autoCreate={false} />}
          {navScreen === "teamsT20" && <Teams format="t20" onClose={() => setNavScreen("home")} autoCreate={false} />}
          {navScreen === "teamsODI" && <Teams format="odi" onClose={() => setNavScreen("home")} autoCreate={false} />}
          {navScreen === "newTeam" && <Teams format="indoor" onClose={() => setNavScreen("home")} autoCreate />}
          {navScreen === "leagues" && (
            <Leagues
              onClose={() => setNavScreen("home")}
              onStartMatchFromFixture={handleStartMatchFromFixture}
              onViewMatch={handleResumeMatch}
              autoCreate={false}
            />
          )}
          {navScreen === "newLeague" && (
            <Leagues
              onClose={() => setNavScreen("home")}
              onStartMatchFromFixture={handleStartMatchFromFixture}
              onViewMatch={handleResumeMatch}
              autoCreate
            />
          )}
          {navScreen === "support" && <Support onClose={() => setNavScreen("home")} />}

          <BottomNav currentScreen={navScreen} onNavigate={handleNavigate} />
        </AppShell>

        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="text-white text-xl">Loading...</div>
          </div>
        )}

        {showModeSelector && (
          <MatchFormatSelector
            onSelect={handleCreateMatchWithMode}
            onCancel={() => setShowModeSelector(false)}
          />
        )}

        {showStartScheduleModal && <StartScheduleModal onStartNow={handleStartNow} onSchedule={handleSchedule} onCancel={() => { setShowStartScheduleModal(false); setSelectedFormat(null); }} />}

        {/* Team format picker modal */}
        {showTeamFormatPicker && (
          <TeamFormatSelector
            onSelect={(fmt) => {
              setShowTeamFormatPicker(false);
              const screenMap: Record<string, NavScreen> = {
                INDOOR: "teamsIndoor",
                T20: "teamsT20",
                ODI: "teamsODI",
              };
              setNavScreen(screenMap[fmt] ?? "teamsIndoor");
              setTimeout(() => setNavScreen("newTeam"), 0);
            }}
            onCancel={() => setShowTeamFormatPicker(false)}
          />
        )}

        {/* Cricket-only hamburger menu */}
        {showMenu && (
          <HamburgerMenu
            onClose={() => setShowMenu(false)}
            onNavigate={(screen) => {
              if (screen === "home") {
                setNavScreen("home");
              } else if (screen === "myMatches") {
                setMyMatchesInitialCategory(null);
                setNavScreen("myMatches");
              } else {
                setNavScreen(screen);
              }
            }}
            onDebug={() => setShowDebugSupabase(true)}
            showDebug={import.meta.env.DEV && localStorage.getItem('DEBUG') === '1'}
          />
        )}

        {/* More menu — tools, resources, legal */}
        {showMore && (
          <MoreMenu
            onClose={() => setShowMore(false)}
            onRules={() => setShowRules(true)}
            onSupport={() => setNavScreen("support")}
            onReportIssue={() => setNavScreen("support")}
            onTerms={() => setShowTerms(true)}
            onAbout={() => setShowAbout(true)}
          />
        )}

        {/* Account menu — personal account */}
        {showAccount && (
          <AccountMenu
            userName={profile?.full_name || user?.email?.split("@")[0] || "Player"}
            userEmail={user?.email}
            onClose={() => setShowAccount(false)}
            onProfile={() => setShowProfile(true)}
            onSettings={() => setShowSettings(true)}
            onSignOut={signOut}
          />
        )}

        {showSettings && <Settings onClose={() => setShowSettings(false)} />}
        {showProfile && (
          <Profile
            onClose={() => setShowProfile(false)}
            onNavigateToPlayers={() => {
              setShowProfile(false);
              setNavScreen("players");
            }}
          />
        )}
        {showMyStats && <MyStats onClose={() => setShowMyStats(false)} />}
        {showAbout && <About onClose={() => setShowAbout(false)} />}
        {showRules && <Rules onClose={() => setShowRules(false)} />}
        {showTerms && <TermsConditions onClose={() => setShowTerms(false)} />}
        {showUserAgreement && <UserAgreement onClose={() => setShowUserAgreement(false)} />}
        {showDebugSupabase && <DebugSupabase />}
      </>
    );
  }

  const oversLabel =
    state.oversLimit === null ? "Unlimited (Test)" : `${state.oversLimit} overs`;

  const tossDisplay = state.toss?.winnerTeam && state.toss?.decision
    ? `${state.toss.winnerTeam === "A" ? state.teamAName : state.teamBName} won toss, chose to ${state.toss.decision === "BAT" ? "bat" : "bowl"}`
    : "Toss not set";

  // Get current inning data for scoreboard
  const currentInning = state.innings[state.currentInnings];
  const overs = currentInning ? Math.floor(currentInning.totalBalls / 6) : 0;
  const balls = currentInning ? currentInning.totalBalls % 6 : 0;
  const currentRunRate = currentInning && currentInning.totalBalls > 0
    ? ((currentInning.totalRuns / currentInning.totalBalls) * 6).toFixed(2)
    : "0.00";
  // Required run rate: only meaningful during a chase (2nd innings with a target).
  // Centralised in the engine so display and logic never diverge.
  const rrrValue = currentInning ? getRequiredRunRate(currentInning) : null;
  const requiredRunRate = rrrValue === null ? null : rrrValue > 36 ? "36+" : rrrValue.toFixed(2);

  const battingTeamName = currentInning?.battingTeam === "A" ? state.teamAName : state.teamBName;
  const isMatchLive = state.status === 'live' || state.status === 'Live' || state.status === 'in_progress';
  const inningsLabel = state.currentInnings === 0 ? "1st Innings" : state.currentInnings === 1 ? "2nd Innings" : `${state.currentInnings + 1}th Innings`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white relative scorebox-container">
      {/* Background layers */}
      <div className="stadium-background"></div>
      <div className="noise-overlay"></div>
      <div className="vignette-overlay"></div>

      {/* Sticky scoreboard bar */}
      <div className="sticky top-0 z-40 scoreboard-bar">
        <div className="max-w-5xl lg:max-w-none mx-auto px-4 lg:px-8 lg:pl-24 py-2.5">
          <div className="flex items-center justify-between gap-3">
            {/* Menu button */}
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="text-slate-300 hover:text-white p-1.5 hover:bg-slate-800/50 rounded-lg transition-colors flex-shrink-0"
              aria-label="Menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* WatchWicket ScoreBox branding + status pill */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {tab === "teams" ? (
                <div className="flex items-center gap-2 min-w-0">
                  <img src={watchwicketLogo} alt="WatchWicket" className="w-7 h-7 object-contain flex-shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-white font-bold text-sm leading-none truncate">WatchWicket ScoreBox</span>
                    <span className="text-emerald-400/70 text-[9px] font-semibold tracking-[0.1em] uppercase leading-none mt-0.5">Match Setup</span>
                  </div>
                </div>
              ) : (
                <>
                  <span className="text-emerald-400 font-bold text-sm hidden sm:inline">WatchWicket ScoreBox</span>
                  {isMatchLive && (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-red-500 font-bold text-[10px] uppercase tracking-wider">LIVE</span>
                      <span className="text-slate-500 text-[10px]">•</span>
                      <span className="text-slate-400 font-semibold text-[10px]">{inningsLabel}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Account avatar */}
            <button
              onClick={() => setShowAccount(true)}
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors"
              style={{ background: "rgba(0,201,128,0.15)", border: "1px solid rgba(0,201,128,0.2)", color: "#00C980" }}
              aria-label="Account"
            >
              {(profile?.full_name || user?.email?.split("@")[0] || "P").slice(0, 2).toUpperCase()}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl lg:max-w-none mx-auto px-2 sm:px-4 lg:px-8 lg:pl-24 py-6 pb-32 lg:pb-10 relative min-h-screen">
        <Watermark />
        <div className="relative z-10">
        {tab === "live" && <LiveScore state={state} setState={setState} isPro={isPro} onMatchComplete={handleMatchComplete} onViewSummary={() => setTab("summary")} onBackToMatches={handleBackToMatches} />}
        {tab === "scorecard" && <Scorecard state={state} />}
        {tab === "summary" && <MatchSummary state={state} isPro={isPro} matchId={currentMatchId || undefined} />}
        {tab === "teams" && (
          <TeamSheet
            state={state}
            setState={setState}
            isPro={isPro}
            onDiscardMatch={handleDiscardMatch}
            onSaveSetup={handleSaveSetup}
            onStartMatch={handleStartMatch}
            matchId={currentMatchId}
            onManageTeams={() => {
              const sf = matchFormatToSquadFormat(state.format);
              setNavScreen(sf === "t20" ? "teamsT20" : sf === "odi" ? "teamsODI" : "teamsIndoor");
            }}
          />
        )}
        {tab === "commentary" && <Commentary state={state} />}
        </div>
      </main>

      <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-20 z-30 pt-20 pb-4 broadcast-side-rail">
        <div className="flex flex-col gap-1 px-2">
          <NavItem icon="🏏" label="Live" active={tab === "live"} onClick={() => setTab("live")} />
          <NavItem icon="📊" label="Card" active={tab === "scorecard"} onClick={() => setTab("scorecard")} />
          <NavItem icon="📝" label="Summary" active={tab === "summary"} onClick={() => setTab("summary")} />
          <NavItem icon="👥" label="Teams" active={tab === "teams"} onClick={() => setTab("teams")} />
          <NavItem icon="💬" label="Comm" active={tab === "commentary"} onClick={() => setTab("commentary")} />
        </div>
      </aside>

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 broadcast-bottom-nav z-50 pb-safe">
        <div className="max-w-5xl mx-auto grid grid-cols-5 px-2 pt-2">
          <NavItem icon="🏏" label="Live" active={tab === "live"} onClick={() => setTab("live")} />
          <NavItem icon="📊" label="Card" active={tab === "scorecard"} onClick={() => setTab("scorecard")} />
          <NavItem icon="📝" label="Summary" active={tab === "summary"} onClick={() => setTab("summary")} />
          <NavItem icon="👥" label="Teams" active={tab === "teams"} onClick={() => setTab("teams")} />
          <NavItem icon="💬" label="Comm" active={tab === "commentary"} onClick={() => setTab("commentary")} />
        </div>
      </nav>

      <style>{`
        /* Broadcast background system */
        .stadium-background {
          position: fixed;
          inset: 0;
          background: linear-gradient(180deg, #0a0f0d 0%, #0d1512 30%, #101a16 60%, #0a0f0d 100%);
          z-index: 0;
        }

        .stadium-background::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 1200px 600px at 20% 20%, rgba(20, 80, 40, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse 1000px 500px at 80% 40%, rgba(20, 80, 40, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse 800px 400px at 50% 80%, rgba(15, 60, 30, 0.1) 0%, transparent 50%);
          z-index: 1;
        }

        .noise-overlay {
          position: fixed;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E");
          opacity: 0.3;
          z-index: 2;
          pointer-events: none;
        }

        .vignette-overlay {
          position: fixed;
          inset: 0;
          background: radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.6) 100%);
          z-index: 3;
          pointer-events: none;
        }

        /* Sticky scoreboard bar */
        .scoreboard-bar {
          background: rgba(10, 15, 13, 0.95);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(16, 185, 129, 0.2);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
        }

        .format-badge {
          font-size: 0.75rem;
          font-weight: 800;
          padding: 0.375rem 0.75rem;
          border-radius: 0.5rem;
          background: rgba(16, 185, 129, 0.15);
          color: rgb(16, 185, 129);
          border: 1px solid rgba(16, 185, 129, 0.3);
          letter-spacing: 0.05em;
        }

        .format-badge-inline {
          font-size: 0.5625rem;
          font-weight: 800;
          padding: 0.125rem 0.5rem;
          border-radius: 0.375rem;
          background: rgba(16, 185, 129, 0.15);
          color: rgb(16, 185, 129);
          border: 1px solid rgba(16, 185, 129, 0.3);
          letter-spacing: 0.05em;
        }

        /* Bottom nav broadcast style */
        .broadcast-bottom-nav {
          background: rgba(10, 15, 13, 0.98);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.6);
        }

        /* Desktop side rail for in-match tabs */
        .broadcast-side-rail {
          background: rgba(10, 15, 13, 0.98);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border-right: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 4px 0 30px rgba(0, 0, 0, 0.5);
        }

        .scorebox-container {
          position: relative;
          overflow-x: hidden;
        }
      `}</style>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {showSettings && <Settings onClose={() => setShowSettings(false)} />}
      {showProfile && (
        <Profile
          onClose={() => setShowProfile(false)}
          onNavigateToPlayers={() => {
            setShowProfile(false);
            setShowCricketSquad(true);
          }}
        />
      )}
      {showCricketSquad && <CricketSquad onBack={() => setShowCricketSquad(false)} />}
      {showAbout && <About onClose={() => setShowAbout(false)} />}
      {showRules && <Rules onClose={() => setShowRules(false)} />}
      {showTerms && <TermsConditions onClose={() => setShowTerms(false)} />}
      {showUserAgreement && <UserAgreement onClose={() => setShowUserAgreement(false)} />}

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="text-white text-xl">Loading...</div>
        </div>
      )}

      {/* Cricket-only hamburger menu (match view) */}
      {showMenu && (
        <HamburgerMenu
          onClose={() => setShowMenu(false)}
          onNavigate={(screen) => {
            handleLeaveMatchAndNavigate(screen);
          }}
        />
      )}

      {/* More menu (match view) */}
      {showMore && (
        <MoreMenu
          onClose={() => setShowMore(false)}
          onRules={() => setShowRules(true)}
          onSupport={() => setNavScreen("support")}
          onReportIssue={() => setNavScreen("support")}
          onTerms={() => setShowTerms(true)}
          onAbout={() => setShowAbout(true)}
        />
      )}

      {/* Account menu (match view) */}
      {showAccount && (
        <AccountMenu
          userName={profile?.full_name || user?.email?.split("@")[0] || "Player"}
          userEmail={user?.email}
          onClose={() => setShowAccount(false)}
          onProfile={() => setShowProfile(true)}
          onSettings={() => setShowSettings(true)}
          onSignOut={signOut}
        />
      )}

      {syncError && (
        <div className="fixed top-4 left-4 right-4 bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded-lg shadow-lg z-[70] max-w-2xl mx-auto">
          <div className="flex items-start gap-3">
            <span className="text-xl">⚠️</span>
            <div className="flex-1">
              <p className="font-semibold mb-1">Sync Error</p>
              <p className="text-sm">{syncError}</p>
            </div>
            <button
              onClick={() => setSyncError(null)}
              className="text-red-200 hover:text-white text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {!isOnline && currentMatchId && (
        <div className="fixed bottom-20 left-4 right-4 bg-yellow-900 border border-yellow-700 text-yellow-100 px-4 py-3 rounded-lg shadow-lg z-[70] max-w-md mx-auto">
          <div className="flex items-start gap-3">
            <span className="text-xl">⚠️</span>
            <div className="flex-1">
              <p className="font-semibold">No Internet Connection</p>
              <p className="text-sm">Your match data is being saved locally and will sync when you're back online.</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function NavItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center py-3 px-2 rounded-lg transition-all duration-200 ${
        active
          ? "bg-green-900/50 text-green-300 font-bold"
          : "text-green-400 hover:text-green-200 hover:bg-green-900/30"
      }`}
    >
      <span className="text-xl mb-1">{icon}</span>
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}
